import { FC, useCallback } from 'react';
import { router } from '@inertiajs/react';

import { Button } from '@/Components/Button';
import { useToast } from '@/hook/useToast';
import { AppModalProps } from '@/types/modal.type';
import { ModalView } from '@/Components/ModalView';
import { PageProps } from '@/types';
import { FlickAnimation } from '@/Components/FlickAnimation';
import { ExclamationSvg } from '@/Components/Icons/ExclamationSvg';

type DeleteFlowModalProps = AppModalProps<{
    flowId: number;
    flowName: string;
}>;

export const DeleteFlowModal: FC<DeleteFlowModalProps> = ({
    payload,
    onClose,
}) => {
    const { showToast, hideToast } = useToast();

    const onUndoDelete = useCallback(() => {
        return new Promise<null>((resolve, reject) => {
            router.post(
                route('flows.undoDestroy', {
                    flowId: payload.flowId,
                }),
                {},
                {
                    onSuccess: () => {
                        hideToast();
                        showToast({
                            autoClose: 5000,
                            variant: 'success',
                            label: `Flow ${payload.flowName} is no longer deleted`,
                        });

                        resolve(null);
                    },
                    onError: (errors) => {
                        hideToast();
                        showToast({
                            autoClose: 5000,
                            variant: 'danger',
                            label: 'Failed to undo delete.',
                        });

                        reject(errors);
                    },
                },
            );
        });
    }, [payload.flowId]);

    const onDelete = useCallback(() => {
        onClose();

        return new Promise<null>((resolve, reject) => {
            router.delete(
                route('flows.destroy', {
                    flow: payload.flowId,
                }),
                {
                    // @ts-expect-error
                    onSuccess: (
                        res: PageProps<{
                            props: {
                                flow_id: number;
                            };
                        }>,
                    ) => {
                        showToast({
                            autoClose: 5000,
                            variant: 'success',
                            label: `Flow ${payload.flowName} has been deleted`,
                            action: {
                                label: 'Undo',
                                href: '#',
                                onClick: (e) => {
                                    e.preventDefault();
                                    onUndoDelete();
                                },
                            },
                        });

                        resolve(null);
                    },
                    onError: (errors) => {
                        Object.keys(errors).forEach((errorKey) => {
                            showToast({
                                autoClose: 5000,
                                variant: 'danger',
                                label: errors[errorKey],
                            });
                        });

                        reject(errors);
                    },
                },
            );
        });
    }, [payload.flowId, payload.flowName, onClose]);

    return (
        <ModalView
            maxWidth="sm"
            onClose={onClose}
            onSubmit={onDelete}
            contentContainerClassName="py-6 px-8 text-brand-ocean flex flex-col justify-center items-center">
            <div className="relative min-w-60 h-24 flex items-center justify-center">
                <FlickAnimation className="absolute z-10 bottom-5 left-11" />
                <div className="absolute bottom-5 right-20">
                    <ExclamationSvg className="[&_path]:fill-orange-500" />
                </div>
            </div>
            <div className="w-full !font-normal text-heading-five text-left text-gray-80 mb-2">
                By deleting this Flow, you will no longer be able to view any
                Performance metrics for it.
            </div>
            <div className="mt-2 w-full flex items-center justify-center space-x-3">
                <Button
                    className="w-full h-9"
                    variant="danger"
                    onClick={onDelete}>
                    Delete Flow
                </Button>
                <Button
                    className="w-full h-9"
                    variant="default"
                    onClick={onClose}>
                    Cancel
                </Button>
            </div>
        </ModalView>
    );
};
